import { useEffect, useState } from 'react'
import FHIR from 'fhirclient'
import Client from 'fhirclient/lib/Client'
import { Accordion, AccordionItem } from './components/Accordion'
import { BottomSheet } from './components/BottomSheet'
import { RiskScoreCard } from './components/RiskScoreCard'
import { useResponsive } from './hooks/useMediaQuery'
import './index.css'

interface PatientData {
  id: string
  name: string
  birthDate: string
  gender: string
}

interface SDOHRisk {
  composite: 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL'
  score: number
  drivers: Array<{
    factor: string
    impact: number
    description: string
  }>
}

function App() {
  const [, setClient] = useState<Client | null>(null)
  const [patient, setPatient] = useState<PatientData | null>(null)
  const [risk, setRisk] = useState<SDOHRisk | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { isMobile, isTablet } = useResponsive()

  useEffect(() => {
    // Check if we're in a SMART launch context or development mode
    const urlParams = new URLSearchParams(window.location.search)
    const isDevelopment = !urlParams.has('code') && !urlParams.has('state')

    if (isDevelopment) {
      // Development mode: Use mock data
      console.log('Running in development mode with mock data')
      const mockPatient: PatientData = {
        id: 'demo-patient-001',
        name: 'Sarah Johnson',
        birthDate: '1978-08-22',
        gender: 'female'
      }
      setPatient(mockPatient)

      // Development mode uses fallback location (no patient address available)
      fetchSDOHRisk(mockPatient.id).then((riskData) => {
        setRisk(riskData)
        setLoading(false)
      })
    } else {
      // Production mode: Initialize SMART on FHIR client
      FHIR.oauth2.ready()
        .then((fhirClient) => {
          setClient(fhirClient)

          // Get patient context
          return fhirClient.patient.read()
        })
        .then((patientResource: any) => {
          const patientData: PatientData = {
            id: patientResource.id,
            name: patientResource.name?.[0]?.text ||
                  `${patientResource.name?.[0]?.given?.[0] || ''} ${patientResource.name?.[0]?.family || ''}`,
            birthDate: patientResource.birthDate || '',
            gender: patientResource.gender || ''
          }
          setPatient(patientData)

          // Extract patient address from FHIR resource
          const address = patientResource.address?.[0]
          const location = address ? {
            city: address.city || 'Unknown',
            state: address.state || 'Unknown',
            zipCode: address.postalCode || '00000',
            countyFips: undefined // Will be geocoded by backend
          } : undefined

          // Fetch SDOH risk from backend service
          return fetchSDOHRisk(patientData.id, location)
        })
        .then((riskData) => {
          setRisk(riskData)
          setLoading(false)
        })
        .catch((err) => {
          console.error('SMART launch error:', err)
          setError(err.message || 'Failed to initialize SMART app')
          setLoading(false)
        })
    }
  }, [])

  const fetchSDOHRisk = async (
    patientId: string,
    patientLocation?: { city: string; state: string; zipCode: string; countyFips?: string }
  ): Promise<SDOHRisk> => {
    // Use patient's actual address from FHIR, or fall back to demo location
    const location = patientLocation || {
      city: 'Columbus',
      state: 'Ohio',
      zipCode: '43215',
      countyFips: '39049' // Franklin County, Ohio (fallback)
    };

    console.log('📍 Patient address extracted:', patientLocation ? 'Real address' : 'Using fallback', location);

    try {
      // Call Peregrine SDOH backend (Step Functions Express)
      const apiUrl = import.meta.env.VITE_SDOH_API_URL;
      console.log('🔧 Environment variable VITE_SDOH_API_URL:', apiUrl);
      console.log('🔧 import.meta.env:', import.meta.env);

      const fullUrl = apiUrl ? `${apiUrl}/api/sdoh/assess` : '/api/sdoh/assess';

      console.log('🔍 Calling SDOH API:', fullUrl);
      console.log('📍 Patient location:', location);

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patientId,
          location,
          includeEnvironmentalJustice: true,
          clinicalContext: 'epic_smart_app'
        })
      });

      console.log('📡 API Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error:', response.status, errorText);
        throw new Error(`SDOH assessment failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ API Response data:', data);
      return data.riskScore;
    } catch (error) {
      console.error('❌ SDOH backend error:', error);
      console.log('⚠️ Falling back to mock data');
      // Fallback to mock data if backend unavailable
      return {
        composite: 'MODERATE',
        score: 67,
        drivers: [
          { factor: 'Housing Instability', impact: 0.28, description: 'High eviction risk in census tract' },
          { factor: 'Food Insecurity', impact: 0.22, description: 'Food desert area, low SNAP access' },
          { factor: 'Transportation Barriers', impact: 0.18, description: 'Limited public transit, vehicle access' },
          { factor: 'Social Isolation', impact: 0.15, description: 'Living alone, low social network' },
          { factor: 'Financial Strain', impact: 0.12, description: 'Below poverty line, medical debt' }
        ]
      };
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-epic-blue rounded-full spinner mx-auto mb-4" />
          <p className="text-gray-600">Loading patient data...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h2 className="text-red-600 font-bold mb-2">Error</h2>
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full overflow-y-auto scrollbar-thin bg-gray-50">
      <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8 pb-24 md:pb-8">
        {/* Patient Header */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 mb-4 md:mb-6">
          <h1 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-900 mb-2">
            SDOH Risk Assessment
          </h1>
          <p className="text-sm md:text-base text-gray-600">
            <strong>{patient?.name}</strong>
            {!isMobile && ` • DOB: ${patient?.birthDate} • ${patient?.gender}`}
          </p>
          {isMobile && (
            <p className="text-xs mt-1 text-gray-500">
              DOB: {patient?.birthDate} • {patient?.gender}
            </p>
          )}
        </div>

        {/* Composite Risk Score */}
        {risk && (
          <div className="mb-4 md:mb-6">
            <RiskScoreCard composite={risk.composite} score={risk.score} />
          </div>
        )}

        {/* Top Risk Drivers */}
        {risk && (
          <div className="card mb-4 md:mb-6">
            <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">
              Top Risk Drivers
            </h2>
            {isMobile || isTablet ? (
              <Accordion>
                {risk.drivers.map((driver, index) => (
                  <AccordionItem
                    key={index}
                    title={driver.factor}
                    impact={driver.impact}
                    defaultOpen={index === 0}
                  >
                    <p className="text-sm text-gray-600">{driver.description}</p>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <div className="flex flex-col gap-4">
                {risk.drivers.map((driver, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex justify-between items-center mb-2">
                      <strong className="text-gray-900">{driver.factor}</strong>
                      <span className="risk-badge-high">
                        Impact: {Math.round(driver.impact * 100)}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{driver.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <BottomSheet>
          <button className="btn-epic-primary w-full md:w-auto">
            Create Referral
          </button>
          <button className="btn-epic-primary w-full md:w-auto">
            Add to Care Plan
          </button>
          <button className="btn-epic-secondary w-full md:w-auto">
            View Full Report
          </button>
          <button className="btn-epic-secondary w-full md:w-auto">
            Schedule Follow-up
          </button>
        </BottomSheet>
      </div>
    </div>
  )
}

export default App
